<template>
  <accordion-item-fi
    v-if="hasTextList || assemblyMedia || instructionPdf"
    ref="accordionItem"
    class="accordion-assembly-instructions-fi"
    header-tag="h3"
    ignore-others
    scroll-to
    @accordion-item-toggle="triggerResize"
  >
    <template #header>
      <template v-if="!isFite">
        {{ $dict.get('product-Catalog.Accordions.Assembly-Instructions') }}
      </template>
      <template v-else>
        {{ $dict.get('product-Catalog.Accordions.Assembly-Instructions-Fite') }}
      </template>
    </template>
    <template #content>
      <grid-columns-fi
        :columns="12"
        grid-class="lg:fi-grid-cols-10"
      >
        <div class="accordion-assembly-instructions-fi__text-container">
          <richtext-fi
            v-if="textListValue(textList, 'KAT_FUNKTION')"
            class="accordion-assembly-instructions-fi__text"
            :html-content="{ value: textListValue(textList, 'KAT_FUNKTION') }"
          />
        </div>

        <div
          v-if="instructionPdf?.Url"
          class="accordion-assembly-instructions-fi__download"
        >
          <button-fi
            :href="instructionPdf.Url"
            target="_blank"
            rel="noopener noreferrer"
            class="accordion-assembly-instructions-fi__download-button button-fi__primary button-fi--small"
            natural-events
            @click="handleDownload"
          >
            <icon-fi icon="download" />
            <template v-if="!isFite">
              {{ $dict.get('product-Catalog.Accordions.Assembly-Instructions-PDF-Button') }}
            </template>
            <template v-else>
              {{ $dict.get('product-Catalog.Accordions.Assembly-Instructions-PDF-Button-Fite') }}
            </template>
          </button-fi>
        </div>
      </grid-columns-fi>

      <div
        v-if="assemblyMedia"
        class="accordion-assembly-instructions-fi__assembly-media"
      >
        <div
          v-for="(item, index) in assemblyMedia"
          :key="item.Name ?? index"
          class="accordion-assembly-instructions-fi__assembly-media--group"
        >
          <div
            v-if="assemblyMediaTitle(item)"
            class="accordion-assembly-instructions-fi__assembly-media-title"
          >
            {{ assemblyMediaTitle(item) }}
          </div>

          <assembly-instructions-media-slider-fi
            :media-items="item.media ?? undefined"
            :title="assemblyMediaTitle(item)"
            :class="{
              'accordion-assembly-instructions-fi__assembly-media-slider': true,
              'accordion-assembly-instructions-fi__assembly-media-slider--no-media-title':
                !assemblyMediaTitle(item) && index !== 0,
            }"
          />
        </div>
      </div>
    </template>
  </accordion-item-fi>
</template>

<script setup lang="ts">
import { AccordionItemFi, ButtonFi, IconFi, RichtextFi } from 'atoms';
import { computed, useTemplateRef } from 'vue';

import AssemblyInstructionsMediaSliderFi from '@/components/catalog-accordions/accordion-assembly-instructions/assembly-instructions-media-slider/AssemblyInstructionsMediaSliderFi.vue';
import GridColumnsFi from '@/components/grid-fi/GridColumnsFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import { useCatalogTextList } from '@/composables/catalogTextList';
import { useEventHub } from '@/composables/eventHub';

defineOptions({ name: 'AccordionAssemblyInstructionsFi' });

interface TextListItem {
  Key?: string | null;
  AttributeKey?: string | null;
  Value?: string | null;
}

interface AssemblyMediaEntry {
  Url: string | null;
  label?: string | null;
  TextList?: TextListItem[] | null;
}

interface AssemblyMediaItem {
  Name: string | null;
  media: AssemblyMediaEntry[] | null;
}

interface AdditionalDocument {
  Type: string | null;
  Url: string | null;
}

interface Props {
  catalogData?: {
    TextList?: TextListItem[] | null;
    AssemblyMedia?: AssemblyMediaItem[] | null;
    Documents?: {
      AdditionalDocuments?: AdditionalDocument[] | null;
    } | null;
  } | null;
}

const props = withDefaults(defineProps<Props>(), {
  catalogData: () => ({}),
});

useBreakpoints();
const { textListValue } = useCatalogTextList();
const { sendEvent } = useEventHub();

const accordionItem = useTemplateRef<any>('accordionItem');

const textList = computed(() => props.catalogData?.TextList);

const hasTextList = computed(() => textListValue(textList.value, 'KAT_FUNKTION'));

const assemblyMedia = computed(() => {
  if (!props.catalogData?.AssemblyMedia) {
    return null;
  }

  const withMedia = props.catalogData.AssemblyMedia.filter(
    (assemblyMedia) => assemblyMedia.media?.length,
  );

  return withMedia.length ? withMedia : null;
});

const instructionPdf = computed(() =>
  props.catalogData?.Documents?.AdditionalDocuments?.find(
    (document) => document.Type === 'Installation Instructions',
  ),
);

const triggerResize = (isOpen: boolean) => {
  if (IS_SSR || !isOpen) {
    return;
  }

  window.dispatchEvent(new Event('resize'));
};

const assemblyMediaTitle = (item: AssemblyMediaItem) => {
  if (!item) {
    return;
  }
  return textListValue(item.media?.[0]?.TextList, 'KAT_NEU_PRODBILD');
};

const handleDownload = () => {
  sendEvent('custom', {}, 'download', {
    link: instructionPdf.value?.Url,
    name: 'Installation Instructions',
  });
};

const openAccordion = () => {
  accordionItem.value?.toggleAccordion();
};

defineExpose({ openAccordion });
</script>

<style src="./accordion-assembly-instructions-fi" />
